import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { InvoiceStatusBadge, InvoiceTypeBadge } from '@/modules/billing/components';
import {
  createInvoiceFromOperation,
  listInvoicesForOperation,
} from '@/modules/billing/services';
import { buildInvoiceNewUrl } from '@/modules/billing/utils/billingUrl';
import type { Invoice } from '@/modules/billing/types';
import { modulePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';

type OperationInvoicesPanelProps = {
  commercialOperationId: string;
  billable: boolean;
};

export function OperationInvoicesPanel({
  commercialOperationId,
  billable,
}: OperationInvoicesPanelProps) {
  const { profile, hasPermission } = useAuth();
  const canRead = hasPermission('billing.read');
  const canWrite = hasPermission('billing.write');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    void listInvoicesForOperation(commercialOperationId)
      .then(setInvoices)
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  }, [commercialOperationId, canRead]);

  async function handleCreateFromOperation() {
    if (!canWrite || !profile) return;
    setCreating(true);
    setError(null);
    try {
      const saved = await createInvoiceFromOperation(
        commercialOperationId,
        profile.uid,
      );
      setInvoices((prev) => [saved, ...prev]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'No se pudo crear la factura.',
      );
    } finally {
      setCreating(false);
    }
  }

  if (!canRead) return null;

  return (
    <div className="inventory-panel">
      <div className="inventory-page-header">
        <h2 className="inventory-panel__subtitle">Facturas</h2>
        {canWrite ? (
          <div className="inventory-actions">
            {billable ? (
              <button
                type="button"
                className="auth-button auth-button--secondary inventory-page-header__btn"
                disabled={creating}
                onClick={() => void handleCreateFromOperation()}
              >
                {creating ? 'Creando…' : '+ Factura desde operación'}
              </button>
            ) : null}
            <Link
              to={buildInvoiceNewUrl({ commercialOperationId })}
              className="auth-button auth-button--secondary inventory-page-header__btn"
            >
              + Nueva factura
            </Link>
          </div>
        ) : null}
      </div>

      {loading ? <p className="auth-muted">Cargando…</p> : null}
      {!loading && invoices.length === 0 ? (
        <p className="auth-muted">Sin facturas vinculadas.</p>
      ) : null}
      {!loading && invoices.length > 0 ? (
        <table className="users-table">
          <thead>
            <tr>
              <th>Número</th>
              <th>Tipo</th>
              <th>Estado</th>
              <th>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id}>
                <td>{inv.invoiceNumber || '—'}</td>
                <td>
                  <InvoiceTypeBadge type={inv.type} />
                </td>
                <td>
                  <InvoiceStatusBadge status={inv.status} />
                </td>
                <td>
                  {inv.total > 0
                    ? `$ ${inv.total.toLocaleString('es-AR')}`
                    : '—'}
                </td>
                <td>
                  <Link
                    to={`${modulePaths.billing}/facturas/${inv.id}`}
                    className="link-button"
                  >
                    Ver
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {error ? <p className="auth-error">{error}</p> : null}
    </div>
  );
}
